<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="店铺ID">
        <el-input v-model="queryParams.shopId" clearable class="!w-140px" />
      </el-form-item>
      <el-form-item label="名称">
        <el-input v-model="queryParams.name" clearable class="!w-160px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-140px">
          <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="店铺" prop="shopId" width="90" />
      <el-table-column label="名称" prop="name" min-width="160" />
      <el-table-column label="状态" prop="status" width="120">
        <template #default="{ row }">{{ statusLabel(row.status) }}</template>
      </el-table-column>
      <el-table-column label="开始" prop="startTime" width="180" :formatter="dateFormatter" />
      <el-table-column label="计划发车" prop="cutTime" width="180" :formatter="dateFormatter" />
      <el-table-column label="自提点" min-width="160">
        <template #default="{ row }">{{ formatPickupPoints(row) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
          <el-button
            v-if="row.status === 'ongoing' || row.status === 'sold_out'"
            link
            type="warning"
            @click="handleCutEarly(row)"
          >本波发车</el-button>
          <el-button
            v-if="row.status === 'ongoing'"
            link
            type="danger"
            @click="handleForceStop(row)"
          >停卖</el-button>
          <el-button
            v-if="row.status === 'draft' || row.status === 'preheat'"
            link
            type="danger"
            @click="handleCancel(row)"
          >取消</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="活动详情（只读）" width="640px">
    <el-descriptions :column="1" border v-if="detail">
      <el-descriptions-item label="名称">{{ detail.name }}</el-descriptions-item>
      <el-descriptions-item label="店铺">{{ detail.shopId }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
      <el-descriptions-item label="开始">{{ formatDateTime(detail.startTime) }}</el-descriptions-item>
      <el-descriptions-item label="计划发车">{{ formatDateTime(detail.cutTime) }}</el-descriptions-item>
      <el-descriptions-item label="计划送达">{{ formatDateTime(detail.planDeliveryTime) }}</el-descriptions-item>
      <el-descriptions-item label="自提点">{{ formatPickupPoints(detail) }}</el-descriptions-item>
      <el-descriptions-item label="分摊运费">{{ detail.deliveryFeeAmount }}</el-descriptions-item>
      <el-descriptions-item label="团长佣金%">{{
        detail.commissionRate != null ? detail.commissionRate : '平台默认'
      }}</el-descriptions-item>
      <el-descriptions-item label="商品数">{{ detail.items?.length || 0 }}</el-descriptions-item>
    </el-descriptions>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/mall/groupbuy/activity'
import { dateFormatter, formatDate } from '@/utils/formatTime'

defineOptions({ name: 'MallGroupbuyActivitySupervise' })

/** 与 B 端活动列表对齐；含历史态只读展示 */
const statusOptions = [
  { label: '草稿', value: 'draft' },
  { label: '预热', value: 'preheat' },
  { label: '进行中', value: 'ongoing' },
  { label: '已售罄', value: 'sold_out' },
  { label: '已截单(历史)', value: 'closed' },
  { label: '配送中(历史)', value: 'delivering' },
  { label: '可提货(历史)', value: 'redeemable' },
  { label: '已结算', value: 'settled' },
  { label: '已取消', value: 'cancelled' }
]

const statusLabel = (status?: string) =>
  statusOptions.find((s) => s.value === status)?.label || status || '-'

/** 接口为毫秒时间戳；失败显示 '-'，绝不回退成原始数字 */
const formatDateTime = (val?: string | number | Date | number[]) => {
  if (val === undefined || val === null || val === '') return '-'
  return formatDate(val as any) || '-'
}

/** 展示自提点名称；没有名称时显示 '-'，不回退成裸 ID */
const formatPickupPoints = (row?: {
  pickupPointNames?: string[]
  pickupPointIds?: number[]
  pickupPointId?: number
}) => {
  if (!row) return '-'
  const names = (row.pickupPointNames || []).filter(Boolean)
  if (names.length) {
    return names.length <= 2 ? names.join('、') : `${names[0]} 等${names.length}个`
  }
  return '-'
}

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  shopId: undefined as any,
  name: undefined as string | undefined,
  status: undefined as string | undefined
})
const detailVisible = ref(false)
const detail = ref<any>()

const getList = async () => {
  loading.value = true
  try {
    const data = await Api.getGroupbuyActivityPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const openDetail = async (id: number) => {
  detail.value = await Api.getGroupbuyActivity(id)
  detailVisible.value = true
}

const handleCutEarly = async (row: any) => {
  await ElMessageBox.confirm(`确认对「${row.name}」执行本波发车？已支付未入批订单将组批。`, '监管发车', {
    type: 'warning'
  })
  await Api.cutEarlyGroupbuyActivity(row.id)
  await getList()
}

const handleForceStop = async (row: any) => {
  await ElMessageBox.confirm(
    `确认停卖「${row.name}」？C 端不可再下单，已入批/在途批次不受影响。`,
    '监管停卖',
    { type: 'warning' }
  )
  await Api.forceStopSellGroupbuyActivity(row.id)
  await getList()
}

const handleCancel = async (row: any) => {
  await ElMessageBox.confirm(`确认取消草稿/预热活动「${row.name}」？`, '监管取消', { type: 'warning' })
  await Api.cancelGroupbuyActivity(row.id)
  await getList()
}

onMounted(getList)
</script>
